import { useEffect, useMemo } from "react";
import { Canvas, useThree } from "@react-three/fiber";
import { Grid, Line, OrbitControls } from "@react-three/drei";
import * as THREE from "three";

/**
 * A six-axis arm drawn from joint angles, so a person can watch a rule being
 * crossed rather than read a number about it.
 *
 * The kinematics here are the same published Denavit-Hartenberg tables the
 * backend uses (cognition-service/rules/counterexample.py, sim-engine/robots.py,
 * robot-bridge/runtime/kinematics.py), and the backend already sends the TCP
 * path it computed. Both are drawn: the link chain from these tables, and the
 * backend's TCP path. If they ever disagree, the mismatch is visible on
 * screen instead of hidden in one or the other.
 */

type DH = Array<[number, number, number, number]>; // theta_offset, d, a, alpha

const HALF_PI = Math.PI / 2;

const DH_TABLES: Record<string, DH> = {
  ur5: [
    [0, 0.089159, 0, HALF_PI],
    [0, 0, -0.425, 0],
    [0, 0, -0.39225, 0],
    [0, 0.10915, 0, HALF_PI],
    [0, 0.09465, 0, -HALF_PI],
    [0, 0.0823, 0, 0],
  ],
  ur10: [
    [0, 0.1273, 0, HALF_PI],
    [0, 0, -0.612, 0],
    [0, 0, -0.5723, 0],
    [0, 0.163941, 0, HALF_PI],
    [0, 0.1157, 0, -HALF_PI],
    [0, 0.0922, 0, 0],
  ],
};

function dhMatrix(theta: number, d: number, a: number, alpha: number): THREE.Matrix4 {
  const ct = Math.cos(theta);
  const st = Math.sin(theta);
  const ca = Math.cos(alpha);
  const sa = Math.sin(alpha);
  // Same layout as _dh_matrix in the Python kinematics modules.
  return new THREE.Matrix4().set(
    ct, -st * ca, st * sa, a * ct,
    st, ct * ca, -ct * sa, a * st,
    0, sa, ca, d,
    0, 0, 0, 1,
  );
}

/** Origin of every joint frame plus the TCP, in the robot base frame (Z up). */
export function jointOrigins(q: number[], dh: DH): THREE.Vector3[] {
  const points: THREE.Vector3[] = [new THREE.Vector3(0, 0, 0)];
  const T = new THREE.Matrix4();
  dh.forEach(([offset, d, a, alpha], i) => {
    T.multiply(dhMatrix((q[i] ?? 0) + offset, d, a, alpha));
    points.push(new THREE.Vector3().setFromMatrixPosition(T));
  });
  return points;
}

export interface ArmViewerProps {
  robotModel: string;
  /** Joint configurations, one per frame. */
  frames: number[][];
  /** Index of the frame currently shown. */
  frame: number;
  /** First frame the rule forbids, or null. */
  violatesAt: number | null;
  /** Backend-computed TCP path, drawn alongside for cross-checking. */
  tcpPath?: number[][];
  /** Workspace box to draw, if the rule is a box: [xmin,xmax,ymin,ymax,zmin,zmax]. */
  box?: number[] | null;
  /** Joint index to highlight, if the rule is a joint range. */
  highlightJoint?: number | null;
  height?: number;
}

/** Z-up robot frame to three.js Y-up, matching the group rotation below. */
function toYUp(p: number[]): THREE.Vector3 {
  return new THREE.Vector3(p[0], p[2], -p[1]);
}

/**
 * Point the camera at the whole motion, not at a guessed spot above the
 * base. A joint sweep can take the tool below the base plane (nothing in the
 * kinematic model says there is a floor), and a fixed target cut that off.
 */
function Framing({ centre, radius }: { centre: THREE.Vector3; radius: number }) {
  const camera = useThree((s) => s.camera);
  useEffect(() => {
    const fov = ((camera as THREE.PerspectiveCamera).fov ?? 40) * (Math.PI / 180);
    const distance = Math.max(0.9, (radius / Math.sin(fov / 2)) * 1.15);
    const dir = new THREE.Vector3(1, 0.65, 1).normalize();
    camera.position.copy(centre.clone().add(dir.multiplyScalar(distance)));
    camera.lookAt(centre);
    camera.updateProjectionMatrix();
  }, [camera, centre, radius]);
  return null;
}

const OK = "#6F8A60";
const BAD = "#B84A3F";
const INK = "#1F1E1D";
const MUTED = "#8A8881";

export default function ArmViewer({
  robotModel,
  frames,
  frame,
  violatesAt,
  tcpPath,
  box,
  highlightJoint,
  height = 360,
}: ArmViewerProps) {
  const dh = DH_TABLES[robotModel.toLowerCase()];
  const forbidden = violatesAt !== null && frame >= violatesAt;

  const origins = useMemo(() => {
    if (!dh || !frames[frame]) return [];
    return jointOrigins(frames[frame], dh);
  }, [dh, frames, frame]);

  const pathBefore = useMemo(() => {
    if (!tcpPath?.length) return [];
    const end = violatesAt ?? tcpPath.length;
    return tcpPath.slice(0, end + 1).map((p) => new THREE.Vector3(p[0], p[1], p[2]));
  }, [tcpPath, violatesAt]);

  const pathAfter = useMemo(() => {
    if (!tcpPath?.length || violatesAt === null) return [];
    return tcpPath.slice(violatesAt).map((p) => new THREE.Vector3(p[0], p[1], p[2]));
  }, [tcpPath, violatesAt]);

  // Bounds of everything drawn: base, the backend's TCP path, the current
  // pose, and the box if there is one.
  const framing = useMemo(() => {
    const b = new THREE.Box3();
    b.expandByPoint(new THREE.Vector3(0, 0, 0));
    (tcpPath ?? []).forEach((p) => b.expandByPoint(toYUp(p)));
    frames.forEach((q, i) => {
      // Sample poses sparsely; the path already covers the tool.
      if (i % 4 === 0 && dh) jointOrigins(q, dh).forEach((o) => b.expandByPoint(toYUp([o.x, o.y, o.z])));
    });
    if (box) {
      b.expandByPoint(toYUp([box[0], box[2], box[4]]));
      b.expandByPoint(toYUp([box[1], box[3], box[5]]));
    }
    const centre = new THREE.Vector3();
    b.getCenter(centre);
    const size = new THREE.Vector3();
    b.getSize(size);
    return { centre, radius: Math.max(0.45, size.length() / 2) };
  }, [tcpPath, frames, dh, box]);

  if (!dh) {
    return (
      <div className="arm-viewer arm-viewer-empty" style={{ height }}>
        No kinematic model for {robotModel}; nothing honest can be drawn.
      </div>
    );
  }

  const boxCentre = box
    ? new THREE.Vector3((box[0] + box[1]) / 2, (box[2] + box[3]) / 2, (box[4] + box[5]) / 2)
    : null;
  const boxSize = box
    ? new THREE.Vector3(box[1] - box[0], box[3] - box[2], box[5] - box[4])
    : null;

  return (
    <div className="arm-viewer" style={{ height }}>
      <Canvas camera={{ position: [1.5, 1.1, 1.5], fov: 40 }} dpr={[1, 2]}>
        <color attach="background" args={["#F5F4EE"]} />
        <Framing centre={framing.centre} radius={framing.radius} />
        <ambientLight intensity={0.9} />
        <directionalLight position={[3, 5, 2]} intensity={0.6} />

        {/* The base: where the arm is bolted, so the eye has an anchor. */}
        <mesh position={[0, -0.02, 0]}>
          <cylinderGeometry args={[0.075, 0.09, 0.04, 32]} />
          <meshStandardMaterial color="#3A3835" />
        </mesh>

        {/* Robot frames are Z-up; three.js is Y-up. One rotation fixes it. */}
        <group rotation={[-Math.PI / 2, 0, 0]}>
          {/* Links */}
          {origins.length > 1 && (
            <Line
              points={origins}
              color={forbidden ? BAD : INK}
              lineWidth={4}
            />
          )}

          {/* Joints */}
          {origins.map((p, i) => {
            const isHighlight = highlightJoint != null && i === highlightJoint;
            const isTcp = i === origins.length - 1;
            return (
              <mesh key={i} position={p}>
                <sphereGeometry args={[isTcp ? 0.028 : isHighlight ? 0.034 : 0.02, 20, 20]} />
                <meshStandardMaterial
                  color={isTcp ? (forbidden ? BAD : OK) : isHighlight ? (forbidden ? BAD : "#C9962E") : INK}
                />
              </mesh>
            );
          })}

          {/* Backend's TCP path, split at the forbidden frame. */}
          {pathBefore.length > 1 && (
            <Line points={pathBefore} color={OK} lineWidth={2} dashed dashSize={0.02} gapSize={0.012} />
          )}
          {pathAfter.length > 1 && (
            <Line points={pathAfter} color={BAD} lineWidth={2.5} />
          )}

          {/* Workspace box, when the rule is one. */}
          {boxCentre && boxSize && (
            <mesh position={boxCentre}>
              <boxGeometry args={[boxSize.x, boxSize.y, boxSize.z]} />
              <meshBasicMaterial color={OK} transparent opacity={0.08} depthWrite={false} />
            </mesh>
          )}
          {boxCentre && boxSize && (
            <lineSegments position={boxCentre}>
              <edgesGeometry args={[new THREE.BoxGeometry(boxSize.x, boxSize.y, boxSize.z)]} />
              <lineBasicMaterial color={OK} />
            </lineSegments>
          )}
        </group>

        <Grid
          args={[4, 4]}
          cellSize={0.1}
          cellColor="#E5E2D8"
          sectionSize={0.5}
          sectionColor="#D4D0C2"
          position={[0, 0, 0]}
          fadeDistance={4}
        />
        <OrbitControls
          makeDefault
          enablePan={false}
          minDistance={0.6}
          maxDistance={4}
          target={framing.centre}
        />
      </Canvas>
      <div className="arm-viewer-caption" style={{ color: forbidden ? BAD : MUTED }}>
        {forbidden ? "forbidden by your rule" : "allowed"}
      </div>
    </div>
  );
}
